import {
  IGetRelatedNicheParams,
  IGifList,
  INiche,
  INicheFeedbackRequest,
  INicheFeedbackResponse,
  INichesCategories,
  IPaginatedNicheList,
  IShortUserList,
} from "./types";

import AuthHttpService from "./AuthHttpService";

interface ISearchNichesRequest {
  query?: string;
  page?: number;
  count?: number;
  order?: "posts" | "subscribers" | "best_match" | "alphabetical_asc" | "alphabetical_desc";
  tags?: string;
}

export default class NichesService extends AuthHttpService {
  public constructor(readonly url?: string) {
    super(url);
  }

  async editNiche(params: {
    id: string;
    description: string;
    image: string | null;
    banner: string | null;
    preferences: string[] | null;
    tags: string[] | null;
  }): Promise<void> {
    return await this.patch(`/v2/niches/${params.id}`, {
      description: params.description,
      image: params.image,
      banner: params.banner,
      preferences: params.preferences,
      tags: params.tags,
    });
  }

  async createNiche(params: {
    name: string;
    description: string;
    rules: string;
    profile_image: string; // upload ticket id
    banner: string | null; // upload ticket id
    tags: string[] | null;
    preferences: string[] | null;
  }): Promise<void> {
    return await this.post("v2/niches", {
      name: params.name,
      description: params.description,
      rules: params.rules,
      profile_image: params.profile_image,
      banner: params.banner,
      tags: params.tags,
      preferences: params.preferences,
    });
  }

  async getNiche(id: string): Promise<INiche> {
    return (await this.get<Promise<{niche: INiche}>>(`v2/niches/${id}`)).niche;
  }

  async addGifNiches(props: {
    gifId: string;
    nicheIds: string[];
  }): Promise<void> {
    await this.put(`v2/gifs/${props.gifId}/niches`, {
      niches: props.nicheIds,
    });
  }

  async removeGifNiches(props: {
    gifId: string;
    nicheIds: string[];
  }): Promise<void> {
    await this.delete(`v2/gifs/${props.gifId}/niches`, {
      niches: props.nicheIds,
    });
  }

  async getFollowingNiches(): Promise<IPaginatedNicheList> {
    return await this.get("v2/niches/following");
  }

  async getMyNiches(): Promise<IPaginatedNicheList> {
    return await this.get("v2/niches/my");
  }

  async getTrendingNiches(variation: string): Promise<IPaginatedNicheList> {
    return await this.get(`v2/niches/trending/${variation}`);
  }

  async getSuggestedNiches(): Promise<INiche[]> {
    return (await this.get<Promise<IPaginatedNicheList>>("v2/niches/suggest")).niches;
  }

  async follow(id: string): Promise<void> {
    await this.post(`v2/niches/${id}/subscription`);
  }

  async unfollow(id: string): Promise<void> {
    await this.delete(`v2/niches/${id}/subscription`);
  }

  async getNicheGifs(params: {
    id: string;
    page: number;
    count: number;
    type?: string|undefined;
    order: string;
    shuffle?: boolean;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    query.append("count", params.count.toString());
    query.append("page", params.page.toString());
    query.append("order", params.order);

    if (params.type !== undefined) {
      query.append("type", params.type);
    }

    if (params.shuffle) {
      query.append("shuffle", "y");
    }

    return await this.get(`v2/niches/${params.id}/gifs?${query}`, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async reportBrokenThumbnail(params: {
    niche: string;
    gif: string;
    thumbnail: string;
  }): Promise<void> {
    await this.post(`v2/niches/${params.niche}/missing-gif`, {
      gif: params.gif,
      thumbnail: params.thumbnail,
    });
  }

  /**
   * Send a feedback for a gif.
   * Valid state values: up, down.
   */
  async addFeedback(params: INicheFeedbackRequest): Promise<INicheFeedbackResponse> {
    return await this.post(`v2/niches/${params.niche}/feedback`, {
      gif: params.gif,
      state: params.state,
    });
  }

  async getRelatedNiches({ niche }: IGetRelatedNicheParams) {
    return await this.get<Promise<IPaginatedNicheList>>(`v2/niches/${niche}/related`);
  }

  async getNichesCreators({ niche }: IGetRelatedNicheParams) {
    return await this.get<Promise<IShortUserList>>(`v2/niches/${niche}/top-creators`);
  }

  async getGifSuggestedNiche(id: string) {
    return await this.get<Promise<IPaginatedNicheList>>(`v2/gifs/${id}/niches/suggest`);
  }

  async getAddGifSuggestedNiches(tags: string[], preference: string) {
    const query = new URLSearchParams({
      tags: tags.join(","),
      preference
    });

    return await this.get<Promise<IPaginatedNicheList>>(`v2/niches/suggest/adding?${query.toString()}`);
  }

  async searchNiches({ query, page, count, order, tags }: ISearchNichesRequest): Promise<IPaginatedNicheList> {
    const params = new URLSearchParams();

    params.append("order", order ?? "subscribers");
    params.append("page", (page ?? 1).toString());

    if (count) {
      params.append("count", count.toString());
    }

    if (query) {
      params.append("query", query);
    }

    if (tags) {
      params.append("tags", tags);
    }

    return await this.get(`v2/niches/search?${params.toString()}`);
  }

  async searchNichesPreviews(params: {
    query?: string;
    order?: string;
    page?: number;
    count?: number;
    tags?: string;
    category?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    query.append("order", params.order ?? "trending");
    query.append("page", (params.page ?? 1).toString());

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.query) {
      query.append("query", params.query);
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    if (params.category) {
      query.append("category", params.category);
    }

    return await this.get(`v2/niches/search/previews?${query}`);
  }

  async getNichesCategories(): Promise<INichesCategories> {
    return await this.get("v2/niches/categories");
  }
}
